import { Button, Dialog, DialogBody, DialogContent, DialogSurface, DialogTitle, dividerClassNames, Spinner } from "@fluentui/react-components";
import { Dismiss24Filled } from "@fluentui/react-icons";
import React, { useMemo } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { getStripeCustomersClient, getUsersClient, getUserTrackingClient } from "../services/dashboard.service";
import { processServerError } from "../utils/helpers/error.helper";
import { EmailType, PricingDialogType, UserBusinessResponseModel } from "../swagger-clients/s365-dashboard-v2-api-clients.service";
import { S365Button } from "./s365-button/s365-button.component";
import { ContactFormModal } from "./contact-form-modal/contact-form-modal.component";
import { toast } from "react-toastify";
import ImpersonateUserService from "../services/impersonate-user.service";
import { RequestViewPricingForm } from "./request-view-pricing-form";

type StripePricingTableDialogProps = {
    isOpened?: boolean;
    title?: string;
    allowUnregisteredAccess?: boolean;
    dialogType: PricingDialogType;
    stripePricingTableId: string;
    stripePricingTableKey: string;
    minDialogWidth?: string;
    minDialogHeight?: string;
    contentHeight: string;
    onClose?: () => void;
}

export const StripePricingTableDialog: React.FC<StripePricingTableDialogProps> = (props) => {
    const [open, setOpen] = React.useState(false);
    const { stripePricingTableId, stripePricingTableKey } = props;
    const [user, setUser] = React.useState<UserBusinessResponseModel>();
    const [isLoadingUserDetails, setIsLoadingUserDetails] = React.useState<boolean>(true);
    const [isLoadingCustomerSession, setIsLoadingCustomerSession] = React.useState<boolean>(true);
    const [customerClientSecret, setCustomerClientSecret] = React.useState<string>(undefined);
    const [showSupportModal, setShowSupportModal] = React.useState<boolean>(false);
    const [viewPricingRequestSubmitted, setViewPricingRequestSubmitted] = React.useState<boolean>(false);
    const pricingTableRef = React.useRef<HTMLDivElement>(null);
    const { instance } = useMsal();
    const isAuthenticated = useIsAuthenticated();

    const account = instance.getActiveAccount();
    React.useEffect(() => {

        if (isAuthenticated) {
            getUserDetails();
            getCustomerSession();
        } else {
            setIsLoadingUserDetails(false);
            setIsLoadingCustomerSession(false);
        }
        //console.log("isAuthenticated changed:", isAuthenticated);

    }, [isAuthenticated]);

    React.useEffect(() => {
        if (!!user && user.readyToOrder && !ImpersonateUserService.IsImpersonatingUser()) {
            logPricingDialogShow();
        }
    }, [user]);

    React.useEffect(() => {
        setOpen(!!props.isOpened);
    }, [props.isOpened]);

    const onClose = () => {
        setOpen(false);

        if (props.onClose) {
            props.onClose();
        }
    }

    const getUserDetails = async () => {
        try {
            setIsLoadingUserDetails(true);
            const client = getUsersClient();
            const userResp = await client.getUserBusinessDetails();
            setUser(userResp);
        }
        catch (error) {
            processServerError(error, undefined, "An error occurred while getting user details.");
        } finally {
            setIsLoadingUserDetails(false);
        }
    }

    const getCustomerSession = async () => {
        try {
            setIsLoadingCustomerSession(true);
            const client = getStripeCustomersClient();
            const customerSessionResp = await client.getCustomerSession();
            if (!!customerSessionResp && !!customerSessionResp.clientSecret) {
                setCustomerClientSecret(customerSessionResp.clientSecret);
            }

        } catch (error) {
            processServerError(error, undefined, "An error occurred while getting customer session.");
        } finally {
            setIsLoadingCustomerSession(false);
        }
    }

    const logPricingDialogShow = async () => {
        try {
            const client = getUserTrackingClient();
            await client.pricingDialogShown(props.dialogType);
        } catch (error) {
            console.log("An error occurred while logging pricing dialog shown action.", error);
        }
    }

    const showPricingTable = useMemo(() => {

        if (!!props.allowUnregisteredAccess)
            return true;

        return !isLoadingUserDetails && !isLoadingCustomerSession && !!user && user.readyToOrder &&
            !!stripePricingTableKey && !!stripePricingTableId && !!account && account.localAccountId;

    }, [user, account, isLoadingUserDetails, isLoadingCustomerSession, props.allowUnregisteredAccess]);

    const shouldDisableBuyButton = useMemo(() => {
        return !!props.allowUnregisteredAccess && !isAuthenticated;
    }, [props.allowUnregisteredAccess, isAuthenticated]);

    React.useEffect(() => {
        if (!shouldDisableBuyButton) {
            return;
        }

        const disableButtons = () => {
            const pricingTable = pricingTableRef.current?.querySelector('stripe-pricing-table') ||
                document.querySelector('stripe-pricing-table');

            if (!pricingTable) {
                return false;
            }

            try {
                const shadowRoot = (pricingTable as any).shadowRoot;
                if (!shadowRoot) {
                    return false;
                }

                const allButtons = shadowRoot.querySelectorAll('button, [role="button"], a[href]');
                const buttonsToDisable = Array.from(allButtons).filter((button) => {
                    return !(button as HTMLElement).classList.contains('Tabs-TabListItem');
                });

                if (buttonsToDisable.length > 0) {
                    buttonsToDisable.forEach((button) => {
                        const htmlButton = button as HTMLElement;
                        htmlButton.style.pointerEvents = 'none';
                        htmlButton.style.opacity = '0.5';
                        htmlButton.style.cursor = 'not-allowed';
                    });
                    return true;
                }

                const iframe = shadowRoot.querySelector('iframe');

                if (iframe) {
                    const existingOverlay = shadowRoot.querySelector('.stripe-button-overlay');
                    if (existingOverlay) {
                        return true;
                    }

                    let wrapper = iframe.parentElement;
                    if (!wrapper || wrapper === shadowRoot) {
                        wrapper = document.createElement('div');
                        wrapper.style.cssText = `
                            position: relative;
                            width: 100%;
                            height: 100%;
                        `;
                        iframe.parentNode?.insertBefore(wrapper, iframe);
                        wrapper.appendChild(iframe);
                    } else {
                        wrapper.style.position = 'relative';
                    }

                    const overlay = document.createElement('div');
                    overlay.className = 'stripe-button-overlay';
                    overlay.style.cssText = `
                        position: absolute !important;
                        top: 50px !important;
                        left: 0 !important;
                        right: 0 !important;
                        bottom: 0 !important;
                        width: 100% !important;
                        height: calc(100% - 50px) !important;
                        background: transparent !important;
                        z-index: 2147483647 !important;
                        cursor: not-allowed !important;
                        pointer-events: auto !important;
                    `;

                    overlay.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.stopImmediatePropagation();
                        return false;
                    }, true);

                    wrapper.appendChild(overlay);
                    return true;
                }
            } catch (error) {
                return false;
            }
            return false;
        };

        let attempts = 0;
        const maxAttempts = 50;
        const intervalId = setInterval(() => {
            attempts++;
            if (disableButtons() || attempts >= maxAttempts) {
                clearInterval(intervalId);
            }
        }, 100);

        return () => {
            clearInterval(intervalId);
        };
    }, [shouldDisableBuyButton, showPricingTable]);


    return <Dialog open={open} onOpenChange={(event, data) => {
        setOpen(data.open);
        if (!data.open) {
            onClose();
        }

    }}>
        <DialogSurface style={{ minWidth: props.minDialogWidth ? props.minDialogWidth : undefined, backgroundColor: "#046795", color: "white" }}>

            <DialogBody style={{ minHeight: props.minDialogHeight ? props.minDialogHeight : undefined }}>
                <DialogTitle action={<Button style={{ color: "white" }} appearance="transparent" onClick={onClose} icon={<Dismiss24Filled />} />}>
                    {props.title}
                </DialogTitle>
                <DialogContent>
                    <div style={{ backgroundColor: "#046795", height: props.contentHeight }}>
                        {/* Mail confirmed, mother ticket exists */}
                        {showPricingTable && <div ref={pricingTableRef} dangerouslySetInnerHTML={{
                            __html: `
                   <stripe-pricing-table pricing-table-id="${stripePricingTableId}"
                        publishable-key="${stripePricingTableKey}" client-reference-id="${account?.localAccountId}" 
                        ${!customerClientSecret && user?.email ? `customer-email="${user.email}"` : ""} 
                        ${!!customerClientSecret && `customer-session-client-secret="${customerClientSecret}"`} >
                    </stripe-pricing-table>`}}>

                        </div>}

                        {/* Mail not confirmed */}
                        {!isLoadingUserDetails && !isLoadingCustomerSession && !!user && !user.isMailConfirmed && !user.readyToOrder && !props.allowUnregisteredAccess &&
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "inherit" }}>
                                <h2 style={{ textAlign: "center", lineHeight: "1.3em" }}>To view pricing and complete your purchase,<br /> please confirm your email address.</h2>
                            </div>}

                        {/* Mail confirmed, no mother ticket */}
                        {/* {!isLoadingUserDetails && !isLoadingCustomerSession && !!user && !!user.isMailConfirmed && !user.readyToOrder && !props.allowUnregisteredAccess &&
                            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "inherit" }}>
                                <h2 style={{ textAlign: "center", lineHeight: "1.3em" }}>We are unable to confirm your professional status.  <br />
                                    If you do not have a business email address,<br /> please submit an alternative form of verification,<br />
                                    such as a link to your LinkedIn profile or other relevant credentials.</h2>
                                <S365Button size="large" onClick={() => { setShowSupportModal(true); }}>Submit proof to support</S365Button>
                            </div>} */}

                        {/* Mail confirmed, no mother ticket */}
                        {!isLoadingUserDetails && !isLoadingCustomerSession && !!user && !!user.isMailConfirmed
                            && !user.readyToOrder && !props.allowUnregisteredAccess && !viewPricingRequestSubmitted &&
                            <div style={{ display: "flex", justifyContent: "center" }}>
                                <RequestViewPricingForm onSubmit={() => { setViewPricingRequestSubmitted(true); }} />
                            </div>}

                        {viewPricingRequestSubmitted && <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "inherit" }}>
                            <h2 style={{ textAlign: "center", lineHeight: "1.3em" }}>
                                Thank you for your request.<br />
                                You'll receive an email once your access to the pricing list has been granted.</h2>
                        </div>}

                        {(isLoadingUserDetails || isLoadingCustomerSession) && !props.allowUnregisteredAccess && <div style={{ display: "flex", justifyContent: "center", height: "100%" }}>
                            <Spinner size="huge" appearance="inverted" label="Loading..." color="white" />
                        </div>}


                    </div>
                    {showSupportModal && <ContactFormModal
                        onHide={() => { setShowSupportModal(false); }}
                        onSuccess={() => {
                            setShowSupportModal(false);
                            toast.success("Successfully sent support ticket.");
                        }} />}

                </DialogContent>
            </DialogBody>
        </DialogSurface>
    </Dialog>
}